import { ActionTree } from 'vuex';
import { MenuState, RootState } from '../types';
import { state } from './menus';
import { state as authState } from '../auth/auth';
import axios, { AxiosInstance } from 'axios';
import { cleanAvailabilities, setAvailability } from '../../utils/availability';
import { formatMenu } from '@/utils/format';
import { handleAllErrors } from '../../utils/errorHandling';
import { decodeMenu } from '@/utils/decoding';
import { getSessionId } from '@/utils/session';
import { DateTime } from 'luxon';
import pako from 'pako';
import i18n from '@/i18n';

export const actions: ActionTree<MenuState, RootState> = {

	/**
	 * Fetch menu groups
	 *
	 * @return {Promise<void>}
	 */
	async fetchMenuGroups({ commit }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const menuGroups = await axiosInst.get('/menu-groups');
			commit('SET_MENU_GROUPS', menuGroups.data);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch restaurant's menus
	 *
	 * @return {Promise<void>}
	 */
	async fetchMenus({ commit }): Promise<void> {
		commit('SET_FETCH_MENUS_COMPLETE', false);
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const menus = await axiosInst.get('/menus/deep-get-menus');
			commit('SET_MENUS', menus.data);
			commit('SET_FETCH_MENUS_COMPLETE', true);
		}
		catch (error) {
			commit('SET_FETCH_MENUS_COMPLETE', true);
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch restaurant's published menus through S3 or local data if in dev mode.
	 * There is a fail safe here if the data is not found in the S3 bucket, we will hit the API call
	 * instead.
	 *
	 * @return {Promise<void>}
	 */
	async fetchPublishedMenus({ commit, dispatch }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		const restaurant: Restaurant = authState.restaurant;
		try {
			let data;
			if (process.env.NODE_ENV === 'development') {
				try {
					data = (await axiosInst.get(`${process.env.VUE_APP_DATA_BUCKET}/${restaurant.slug}/data.json`)).data;
				}
				catch (e) {
					if (process.env.VUE_APP_USE_LOCAL_DB === 'true') {
						try {
							data = require(`../../data.${restaurant.slug}-local-db.json`);
						}
						catch (e) {
							return dispatch('fetchPublishedMenusAPI');
						}
					}
					else {
						return dispatch('fetchPublishedMenusAPI');
					}
				}
			}
			else {
				try {
					const s3Data = (await axiosInst.get(`${window.location.origin}/${restaurant.slug}/data.json`)).data;

					// Validate response content - S3 will not throw a 404 if the file is not found
					if (!s3Data || !s3Data.menus || !Array.isArray(s3Data.menus) || s3Data.menus.length === 0) {
						return dispatch('fetchPublishedMenusAPI');
					}
					data = s3Data;
				}
				catch (error) {
					return dispatch('fetchPublishedMenusAPI');
				}
			}
			commit('SET_PUBLISHED_MENUS', data.menus);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch restaurant's published menus. This logic is different than most of our APIs.
	 * We are fetching a binary response from the API, which is a Base64 encoded string.
	 * This is because we can't do a proper GZIP content-encoding due API Gateway not supporting it
	 * with HTTP API. So we have to do this hack.
	 *
	 * For that reason we have to decode the Base64 string, convert it to a Uint8Array, and then
	 * decompress it using pako. We then parse the JSON string and commit it to the state.
	 * However if the body is not Base64 encoded, we assume it's a plain JSON string and parse it (for smaller data).
	 *
	 * @return {Promise<void>}
	 */
	async fetchPublishedMenusAPI({ commit }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			let data;
			const response = await axiosInst.get('/menus/get-published-menus-optimized');
			const { body, isBase64Encoded } = response.data;

			// Backwards compatibility for cache issues, can be removed later (current date: 2025-03-14)
			if(!body) {
				return commit('SET_PUBLISHED_MENUS', response.data);
			}
			if (isBase64Encoded) {
				// The body is a Base64 encoded string and decode Base64 into a binary string.
				const binaryString: string = atob(body);
				// Convert the binary string to a Uint8Array.
				const len: number = binaryString.length;
				const uint8Array: Uint8Array = new Uint8Array(len);
				for (let i = 0; i < len; i++) {
					uint8Array[i] = binaryString.charCodeAt(i);
				}
				// Decompress (we assume it's gzip compressed here)
				const decompressed = pako.ungzip(uint8Array, { to: 'string' });
				data = JSON.parse(decompressed);
			}

			// If not Base64 encoded, assume the body is a plain JSON string.
			else {
				data = body;
			}

			commit('SET_PUBLISHED_MENUS', data);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Get a menu by id with all its sections, items, options and sub-options
	 *
	 * @param {number} menuId
	 * @return {Promise<Menu | void>}
	 */
	async deepGetMenu({}, menuId: number): Promise<Menu | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.get<Menu>(`/menus/deep-get-menu/${menuId}`);
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch restaurant's sections
	 *
	 * @param {Menu} menu
	 * @return {Promise<void>}
	 */
	async fetchSections({ commit }, menu: Menu): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const sections = (await axiosInst.get(`/menus/${menu.id}/sections/deep-get-sections`)).data;
			commit('SET_SECTIONS', { menu, sections });
			commit('SET_SELECTED_MENU_SECTIONS', { menu, sections });
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch restaurant's menu items
	 *
	 * @param {Menu} menu
	 * @return {Promise<void>}
	 */
	async fetchMenuItems({ commit }, menu: Menu): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const items = await axiosInst.get(`/menus/${menu.id}/items`);
			commit('SET_MENU_ITEMS', { menu, items: items.data });
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch restaurant's menu section items
	 *
	 * @param {Menu} menu
	 * @param {MenuSection} section
	 * @return {Promise<void>}
	 */
	async fetchMenuSectionItems({ commit }, { menu, section }: { menu: Menu, section: MenuSection}): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			section.menu_id = menu.id;
			const items = (await axiosInst.get(`/menus/${menu.id}/sections/${section.id}/items`)).data;
			commit('SET_SECTION_ITEMS', { section, items });
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Create menu group
	 *
	 * @param {MenuGroup} menuGroup
	 * @param {number[]} selectedMenus
	 * @param {File} imageFile
	 * @return {Promise<void>}
	 */
	async createMenuGroup({ dispatch, commit }, { menuGroup, selectedMenus, imageFile } : { menuGroup: MenuGroup, selectedMenus: number[], imageFile: File }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const menuGroupPosted: MenuGroup = (await axiosInst.post('/menu-groups', { ...menuGroup, menus: selectedMenus })).data;
			if(imageFile) {
				await dispatch('uploadMenuGroupImage', { menuGroup: menuGroupPosted, image: imageFile });
			}
			dispatch('fetchMenuGroups');

			// Fetch menus
			const menus = await axiosInst.get('/menus/deep-get-menus');
			commit('SET_MENUS', menus.data);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Upload image to S3 and update the menu group's image accordingly
	 *
	 * @param {MenuGroup} menuGroup
	 * @param {any[]} image
	 * @return {Promise<void>}
	 */
	async uploadMenuGroupImage ({ dispatch }, { menuGroup, image } : { menuGroup: MenuGroup, image: any[] }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		const formData = new FormData();
		formData.append('file', image[0], image[0].name);

		try {
			await axiosInst.post(`/menu-groups/${menuGroup.id}/image`, formData, {
				headers: {
					'Content-Disposition': `form-data; name="file"; filename="${image[0].name}"`,
					'Content-Type': 'multipart/form-data'
				}
			});
		}
		catch (error) {
			dispatch('fetchMenuGroups');
			handleAllErrors(error);
		}
	},

	/**
	 * Update a menu & its availabilities if any
	 *
	 * @param {MenuGroup} menuGroup
	 * @param {number[]} selectedMenus
	 * @param {File} imageFile
	 * @return {Promise<void>}
	 */
	async updateMenuGroup({ dispatch, commit }, { menuGroup, selectedMenus, imageFile } : { menuGroup: MenuGroup, selectedMenus: number[], imageFile: File }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const menuGroupUpdated: MenuGroup = (await axiosInst.put(`/menu-groups/${menuGroup.id}`, { ...menuGroup, menus: selectedMenus }))?.data;
			if(imageFile) {
				await dispatch('uploadMenuGroupImage', { menuGroup: menuGroupUpdated, image: imageFile });
			}
			dispatch('fetchMenuGroups');

			// Fetch menus
			const menus = await axiosInst.get('/menus/deep-get-menus');
			commit('SET_MENUS', menus.data);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Delete a menu
	 *
	 * @param {MenuGroup} menuGroup
	 * @return {Promise<void>}
	 */
	async deleteMenuGroup({ dispatch, commit }, menuGroup: MenuGroup): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			await axiosInst.delete(`/menu-groups/${menuGroup.id}`);
			dispatch('fetchMenuGroups');

			// Fetch menus
			const menus = await axiosInst.get('/menus/deep-get-menus');
			commit('SET_MENUS', menus.data);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Set selected menu and fetch the availability of that menu
	 *
	 * @param {Menu} selectedMenu
	 */
	async setSelectedMenu({ commit }, selectedMenu: Menu): Promise<void> {
		selectedMenu = decodeMenu(selectedMenu);
		if(selectedMenu.name && selectedMenu.availability) {
			selectedMenu.availability = setAvailability(selectedMenu.availability);
		}

		commit('SET_SELECTED_MENU', selectedMenu);
	},

	/**
	 * Create menu
	 *
	 * @param {Menu} menu
	 * @param {MenuAvailability} availability
	 * @param {File} imageFile
	 * @return {Promise<void>}
	 */
	async createMenu({ dispatch }, { menu, availability, imageFile }: { menu: Menu, availability: MenuAvailability[], imageFile: File }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const menuPosted: Menu = (await axiosInst.post('/menus', menu)).data;
			if (availability.length) {

				// Temporary solution until API is updated.
				await axiosInst.post(`/menus/${menuPosted.id}/availability`, availability)
					.catch((error) => {
						dispatch('deleteMenu', menuPosted);
						handleAllErrors(error);
					});
			}
			if(imageFile) {
				await dispatch('uploadMenuImage', { menu: menuPosted, image: imageFile });
			}
			dispatch('fetchMenus');
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Upload image to S3 and update the menu's image accordingly
	 *
	 * @param {Menu} menu
	 * @param {any[]} image
	 * @return {Promise<void>}
	 */
	async uploadMenuImage ({ dispatch }, { menu, image } : { menu: Menu, image: any[] }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		const formData = new FormData();
		formData.append('file', image[0], image[0].name);

		try {
			await axiosInst.post(`/menus/${menu.id}/image`, formData, {
				headers: {
					'Content-Disposition': `form-data; name="file"; filename="${image[0].name}"`,
					'Content-Type': 'multipart/form-data'
				}
			});
		}
		catch (error) {
			dispatch('fetchMenus');
			handleAllErrors(error);
		}
	},

	/**
	 * Update a menu & its availabilities if provided
	 *
	 * @param {Menu} menu
	 * @param {MenuAvailability[]} [availability] - Only sent when availability has changed
	 * @param {File} imageFile
	 * @return {Promise<void>}
	 */
	async updateMenu({ dispatch }, { menu, availability, imageFile } : { menu: Menu, availability?: MenuAvailability[], imageFile: File }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const menuUpdated: Menu = (await axiosInst.put(`/menus/${menu.id}`, menu))?.data;
			if (availability !== undefined) {
				await axiosInst.post(`/menus/${menuUpdated.id}/availability`, availability);
			}
			if(imageFile) {
				await dispatch('uploadMenuImage', { menu: menuUpdated, image: imageFile });
			}
			dispatch('fetchMenus');
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Delete a menu
	 *
	 * @param {Menu} menu
	 * @return {Promise<void>}
	 */
	async deleteMenu ({ commit }, menu: Menu): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			await axiosInst.delete(`/menus/${menu.id}`);
			commit('SET_UPDATED_MENUS', { deletedMenuId: menu.id });
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Update menus availabilities if any
	 *
	 * @param {Menu} menu
	 * @param {MenuAvailability[]} availability
	 * @return {Promise<void>}
	 */
	async updateMenuAvailability({ dispatch }, { menu, availability } : { menu: Menu, availability: MenuAvailability[]}): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			if (availability.length) {
				await axiosInst.post(`/menus/${menu.id}/availability`, availability);
				dispatch('fetchMenus');
			}
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Copy the menu availability
	 *
	 * @param {Menu} menu
	 * @param {number[]} menuIds
	 * @return {Promise<void>}
	 */
	async copyMenuAvailability({ dispatch }, { menu, menuIds } : { menu: Menu, menuIds: number[]}): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const availability = cleanAvailabilities(menu.availability);
			if (availability.length && menuIds?.length) {
				await axiosInst.post(`/menus/${menu.id}/availability/copy`, { availability, menuIds });
				dispatch('fetchMenus');
			}
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Duplicate a menu copy
	 *
	 * @param {Menu} menu - copying
	 * @return {Promise<void>}
	 */
	async duplicateMenu ({ dispatch }, menu: Menu): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			const newMenuPayload = formatMenu({
				...menu,
				id: undefined,
				name: menu.name + i18n.t('shared.copy'),
				published_at: null,
				published: false
			} as Menu);
			const newMenu = formatMenu((await axiosInst.post('/menus/', newMenuPayload)).data) as Menu;
			if (menu.availability && menu.availability.length){
				await axiosInst.post(`/menus/${newMenu.id}/availability`, menu.availability);
			}
			if (menu.items){
				for (const item of menu.items) {
					await dispatch('items/duplicateMenuItem', { oldMenu: menu, newMenu: newMenu, item, appendCopy: false }, { root: true });
				}
			}
			if (menu.sections){
				for (const section of menu.sections) {
					await dispatch('sections/duplicateSection', { oldMenu : menu, newMenu: newMenu, section, appendCopy: false },{ root: true });
				}
			}
			dispatch('fetchMenus');
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Pull integrated menu(s) from Salata
	 * We start by making the API call, if the API call succeeds we simply fetch the menu
	 * again. However if the API call fails, it doesn't mean that the request failed to go
	 * through. If the API returns a 504, this most likely means that the Gateway timed out
	 * after 30 seconds. We cannot increase this timeout nor should we since we don't want
	 * requests to keep going for minutes for cost purposes. So, when the call returns a 504,
	 * we start an interval that can be ended by two conditions. 1: 5 minutes timeout if nothing
	 * works. 2: we keep fetching the menus, and checking if the updated_at date is now newer than
	 * the date before the pull, if it is, it means the pull was successful and we can now re-fetch.
	 *
	 * @param {MenuPullPayload} payload
	 * @return {Promise<any>}
	 */
	async pullMenus({ dispatch, commit }, payload: MenuPullPayload): Promise<any> {
		const axiosInst = axios.create({
			baseURL: process.env.VUE_APP_API_AUDIENCE,
			headers: {
				'X-RESTAURANT-ID': payload.id!,
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${authState.accessToken}`,
				'X-SESSION-ID': getSessionId()
			}
		});

		return await new Promise((resolve, reject) => {
			const localTime: string = DateTime.local().toISO();
			state.pulledLocationName = payload.name;
			commit('SET_FETCH_MENUS_COMPLETE', false);
			axiosInst.put(`/restaurants/upload-menu/${payload.id}`, { published_at: '2020-05-29 01:40:58' }) // Published_at date to trick event bridge
				.then(response => {
					// We let the pull all method fetch the menus once all the locations have been pulled
					if(!payload.pullAll) {
						dispatch('fetchMenus');
					}
					state.pulledLocationName = null;
					resolve(response);
				})
				.catch(error => {
					const tempError: any = error;

					// Service timeout from API Gateway
					if(tempError?.response?.data?.statusCode === 503 || tempError?.response?.data?.statusCode === 504 || tempError?.response?.status === 503 || tempError?.response?.status === 504 || tempError.message === 'Network Error') {
						// Grab updated_at date before pull and initiate counter (if no menus, we set the date to the local time)
						const currentUpdatedAt: string = state.menus[0] ? state.menus[0].updated_at! : localTime;
						let nextUpdatedAt: string = currentUpdatedAt;
						let counter: number = 0;

						// Start the interval and fetch the menus every 10 seconds until either we get to 5 minutes
						// or we have new menus
						const interval = window.setInterval(async () => {
							counter += 10; // set up seconds/minutes

							// Get fetched updated_at date
							const menus = await axiosInst.get('/menus');
							if(menus?.data[0]?.updated_at) {
								nextUpdatedAt = menus.data[0].updated_at;
							}

							// Check if we are at 5 minutes or the menu pull succeeded.
							if((DateTime.fromISO(currentUpdatedAt).toMillis() < DateTime.fromISO(nextUpdatedAt).toMillis()) || counter >= 300){
								clearInterval(interval);
								// We let the pull all method fetch the menus once all the locations have been pulled
								if(!payload.pullAll) {
									dispatch('fetchMenus');
								}
								state.pulledLocationName = null;

								// If we got here due to the 5 minutes timeout, then something did go wrong with the menu pull
								if(counter >= 300) {
									reject();
								}

								// If we got here because of the updated_at date difference, means we got a successful pull
								resolve('success');
							}
						}, 10000);
					}
					else {
						// We let the pull all method fetch the menus
						if(!payload.pullAll) {
							dispatch('fetchMenus');
						}
						state.pulledLocationName = null;
						reject(tempError);
					}
				});
		});
	},

	/**
	 * A call to the Remy to build the local menu
	 * On success a file will be written to the local file system, see the build-local-menu.ts in Remy for more info.
	 *
	 * @return {Promise<void>}
	 */
	async buildLocalMenu({}, restaurantId: string): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			if (process.env.NODE_ENV !== 'development') {
				throw new Error('This feature is only available in development environment');
			}
			// Override X-RESTAURANT-ID so the backend builds the menu against the target tenant's schema,
			// not the currently-selected tenant the shared axios instance is pinned to.
			await axiosInst.get(`/menus/build-local-menu/${restaurantId}`, {
				headers: { 'X-RESTAURANT-ID': restaurantId }
			});
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async fetchRotationalMenus({ commit }, { startDate, endDate }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const menus = await axiosInst.get<RotationalMenu[]>(`/rotational-menus?start_date=${startDate}&end_date=${endDate}`);
			commit('SET_ROTATIONAL_MENUS', menus.data);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Send CSV file to create the menus, sections and items.
	 * * The restaurant ID is part of the payload as it can be a restaurant other than the selected one
	 *
	 * @param {{ restaurantId: string, file: File }} payload
	 * @return {Promise<{ [key: string]: number } | undefined>}
	 */
	async importCsv({ dispatch }, payload: { restaurantId: string, file: File }): Promise<{ [key: string]: number } | undefined> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		const formData = new FormData();
		formData.append('file', payload.file);

		try {
			const { data } = await axiosInst.post<{ [key: string]: number }>('/menus/csv-menus-import', formData, {
				headers: {
					'X-RESTAURANT-ID': payload.restaurantId,
					'Content-Disposition': `form-data; name="file"; filename="${payload.file.name}"`,
					'Content-Type': 'multipart/form-data'
				}
			});
			dispatch('fetchMenus');
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async createRotationalMenu({ commit }, payload: RotationalMenuPayload): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const newMenu = await axiosInst.post<RotationalMenu>('/rotational-menus', payload);
			commit('UPDATE_ROTATIONAL_MENU', newMenu.data);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async bulkCreateRotationalMenus({}, payload: RotationalMenuPayload[]): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.post<RotationalMenu[]>('/rotational-menus/bulk', payload);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async updateRotationalMenu({ commit }, payload: RotationalMenuPayload): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		const { id } = payload;

		// Don't send put request if rotational menu doesn't have an id (hasn't been created)
		if (id) {
			try {
				const { data } = await axiosInst.put<RotationalMenu>(`/rotational-menus/${id}`, payload);

				// If response is empty, it means the menu was deleted
				if (data) {
					commit('UPDATE_ROTATIONAL_MENU', data);
				}
				else {
					commit('REMOVE_ROTAIONAL_MENU', payload.id);
				}
			}
			catch (error) {
				handleAllErrors(error);
			}
		}
	},

	async getMenuIdsFromRotationalMenuItems({}): Promise<number[]> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.get<number[]>('/rotational-menus/item-menus-list');
			return data;
		}
		catch (error) {
			handleAllErrors(error);
			return [];
		}
	},

	/**
	 * Deletes any rotational menus that are older than 7 days or have no items
	 *
	 * @return {Promise<void>}
	 */
	async cleanupRotationalMenus({}): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.delete('/rotational-menus/cleanup');
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Deletes abandoned open items (older than 24 hours), fired when entering the ordering flow
	 *
	 * @return {Promise<void>}
	 */
	async cleanupOpenItems({}): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.delete('/menus/open-items/cleanup');
		}
		catch {
			// Silently fail, an error shouldn't block ordering flow and user doesn't need to see the error
		}
	}
};
